import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';
import styled from 'styled-components';
import { Button, Switch, Table } from '@components/Common';
import CheckMark from '@components/Common/CheckMark';
import {
  ButtonColumn,
  DateFilterControlled,
  FilterContainer,
  TankSelectorControlled,
} from '@components/Common/EntryFilters';
import ContentLoader, { ContentContainer } from '@components/Common/Loaders/ContentLoader';
import { Columns, Info, Warning } from '@components/Common/SharedStyles';
import { useAuth } from '@components/MsalProvider';
import { entryOrderSortByString } from '@helpers/sorting';
import { compareDatesAsc } from '@helpers/dateUtils';
import { useApprovalFilters } from '@hooks/useApprovalFilters';
import { UserRole } from '~types/types';
import {
  getAmountColumn,
  getDirectionColumn,
  getEntrySourceColumn,
  getEntryStateColumn,
  getEntryTypeColumn,
  getUnloadingFinishedColumn
} from '../Common/entryColumns';
import Modes from '../Common/InventoryNavigation';
import { TankInformation } from './TankInformation';

// TanStack Query hooks
import { useTanksQuery } from '@api/queries/tanks';
import { useTerminalsQuery } from '@api/queries/terminals';
import { useApprovalQuery, useApproveMutation, BookkeepingEntry } from '@api/queries/approval';

const ApprovalList: React.FC = () => {
  const { t } = useTranslation();

  // Local UI state
  const [useAdminOverride, setUseAdminOverride] = useState(false);

  // User role from auth context
  const { role } = useAuth();
  const isAdmin = role === UserRole.Admin;

  // Filters from URL search params
  const { filters, tankFilter, dateFilter, setTankFilter, setDateFilter } = useApprovalFilters();
  const selectedTankId = filters.tankId;
  const endDate = filters.endDate;

  // TanStack Query for data fetching
  const { data: tanks = [] } = useTanksQuery();
  const { data: terminals = [] } = useTerminalsQuery();
  const {
    data: approvalData,
    isLoading: isLoadingEntries,
    refetch
  } = useApprovalQuery(selectedTankId, useAdminOverride);

  // Mutations
  const approveMutation = useApproveMutation();
  const isApproving = approveMutation.isPending;

  // Find selected tank
  const selectedTank = useMemo(() => {
    return selectedTankId ? tanks.find(tank => tank.id === selectedTankId) : null;
  }, [selectedTankId, tanks]);

  // Refetch when admin override changes
  useEffect(() => {
    if (selectedTankId) {
      refetch();
    }
  }, [useAdminOverride]);

  // Extract data from approval query
  const entries = approvalData?.entries ?? [];
  const approvable = approvalData?.approvable ?? {};
  const balances = approvalData?.balances ?? {};
  const approvedBalance = approvalData?.approvedBalance;
  const entriesTankId = approvalData?.tankId;

  // Filter entries by end date
  const filteredEntries = useMemo(() => {
    return endDate ? entries.filter(entry => compareDatesAsc(entry.order, endDate) <= 0) : entries;
  }, [entries, endDate]);

  const hasApprovableEntries = !!filteredEntries.find(e => approvable[e.id]);
  const canApprove = !isApproving && !isLoadingEntries && selectedTank !== null && hasApprovableEntries;

  const handleApprove = () => {
    if (!confirm(t('approval.confirm_approval'))) {
      return;
    }

    const approvableEntries = filteredEntries
      .filter(e => approvable[e.id])
      .sort(entryOrderSortByString);

    if (approvableEntries.length > 0) {
      const until = approvableEntries[approvableEntries.length - 1].order;
      approveMutation.mutate({
        tankId: entriesTankId!,
        until,
        useAdminOverride
      });
    }
  };

  const columns: Column[] = [
    {
      Header: 'Id',
      id: 'id',
      accessor: 'id',
      show: false
    },
    {
      Header: t('approval.approvable'),
      id: 'isApprovable',
      width: 100,
      filterable: false,
      accessor: 'id',
      Cell: ({ value }) => <CheckMark active={approvable[value]} emphasize />
    },
    getEntrySourceColumn(t),
    getEntryTypeColumn(t),
    getEntryStateColumn(t),
    {
      Header: t('entry.customer'),
      id: 'customerName',
      accessor: 'customerName',
      filterable: false
    },
    {
      Header: t('entry.product'),
      id: 'productName',
      accessor: 'productName',
      filterable: false
    },
    getAmountColumn(t),
    getDirectionColumn(t),
    {
      Header: () => (
        <span>
          {t('approval.balance')} <sup>kg</sup>
        </span>
      ),
      id: 'balance',
      maxWidth: 120,
      filterable: false,
      accessor: (entry: BookkeepingEntry) =>
        typeof balances[entry.id] === 'number' ? balances[entry.id].toLocaleString('fi') : ''
    },
    getUnloadingFinishedColumn(t),
    {
      Header: t('entry.notes'),
      id: 'notes',
      accessor: 'notes',
      filterable: false
    }
  ];

  return (
    <>
      <Modes isEnabled={!!selectedTankId} fetch={() => refetch()} isLoading={isLoadingEntries} />
      <FilterContainer>
        <Columns>
          <TankSelectorControlled
            terminals={terminals}
            tanks={tanks}
            value={tankFilter}
            onChange={setTankFilter}
          />
          <DateFilterControlled
            value={dateFilter}
            onChange={setDateFilter}
            isStartDisabled={true}
            startedPlaceholder={t('approval.filter_last_approval')}
          />
          <ButtonColumn className="column">
            <Button tertiary={true} disabled={!canApprove} onClick={handleApprove}>
              {t('approval.approve')}
            </Button>
            <div>
              <Info>
                {t('approval.approvable_entries_marked')} <CheckMark active={true} emphasize inline />
              </Info>
            </div>
          </ButtonColumn>
        </Columns>
        {isAdmin && (
          <>
            <div>
              <Switch
                onChange={setUseAdminOverride}
                checked={useAdminOverride}
                id="useAdminOverride"
                label={t('approval.use_admin_override')}
              />
            </div>
            {useAdminOverride && <Warning>{t('approval.admin_override_warning')}</Warning>}
          </>
        )}
        {selectedTank && <TankInformation balance={approvedBalance} t={t} />}
      </FilterContainer>
      <ContentContainer>
        {isLoadingEntries && <ContentLoader />}
        {selectedTank && (
          <Table
            data={filteredEntries}
            columns={columns}
            defaultSortColumn="unloadingFinished"
            defaultSortDesc={false}
            filterable={false}
            noDataText={t('entry.no_rows_bookkeeping_approving')}
          />
        )}
      </ContentContainer>
    </>
  );
};

export default ApprovalList;
